import * as Constants from '../components/Constants.js';

// Small wrapper around fetch to normalize responses across the app.
const buildUrl = (path) => {
  if (!path) return Constants.URLBASE;
  return Constants.URLBASE.replace(/\/+$/, '') + '/' + String(path).replace(/^\/+/, '');
};

async function handleResponse(response) {
  const status = response.status;

  if (status === 401) {
    // Notify the app via event so React Router navigates client-side (avoids CloudFront 403 on full-page reload)
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
    return { ok: false, status };
  }

  if (response.redirected) {
    document.location = response.url;
    return { ok: false, status };
  }

  if (status === 204) {
    return { ok: true, data: null, status, headers: response.headers };
  }

  // try to parse JSON, but tolerate empty bodies
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  // If backend uses wrapper {ok,data,errors} respect it
  if (json && ('ok' in json || 'data' in json || 'errors' in json || 'errores' in json)) {
    return {
      ok: !!json.ok,
      data: json.data ?? null,
      errors: json.errors ?? json.errores ?? null,
      status,
      headers: response.headers,
    };
  }

  // For other responses return parsed body as data
  return { ok: response.ok, data: json, status, headers: response.headers };
}

async function request(path, options = {}) {
  const url = buildUrl(path);
  const options_ = { redirect: 'follow', credentials: 'include', ...options };

  try {
    const response = await fetch(url, options_);
    const result = await handleResponse(response);
    return result;
  } catch (error) {
    console.error('API client error', error);
    return { ok: false, data: null, errors: ['Network error'], status: null };
  }
}

const get = (path) => request(path, { method: 'GET' });
const post = (path, body) =>
  request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
const patch = (path, body) =>
  request(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
const put = (path, body) =>
  request(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
const deleteMethod = (path) => request(path, { method: 'DELETE' });

const api = { request, get, post, patch, put, delete: deleteMethod };

export default api;
export { request, get, post, patch, put, deleteMethod as delete };
